export type EylfSubcode = {
  id: string;
  en: string;
  zh: string;
};

export type EylfOutcome = {
  number: 1 | 2 | 3 | 4 | 5;
  title_en: string;
  title_zh: string;
  subcodes: EylfSubcode[];
};

export const EYLF_OUTCOMES: EylfOutcome[] = [
  {
    number: 1,
    title_en: "Children have a strong sense of identity",
    title_zh: "兒童形成強烈的身份認同感",
    subcodes: [
      { id: "1.1", en: "Children feel safe, secure, and supported", zh: "兒童感覺安全、可靠和受支持" },
      { id: "1.2", en: "Children develop their emerging autonomy, interdependence, resilience and sense of agency", zh: "兒童日益養成獨立性、相互依賴性、適應力和主導感" },
      { id: "1.3", en: "Children develop knowledgeable and confident self-identities", zh: "兒童形成對自我身份的認識和自信" },
      { id: "1.4", en: "Children learn to interact in relation to others with care, empathy and respect", zh: "兒童學會關心、憐憫、尊重地與他人互動" },
    ],
  },
  {
    number: 2,
    title_en: "Children are connected with and contribute to their world",
    title_zh: "兒童能連結自己的世界並做出貢獻",
    subcodes: [
      { id: "2.1", en: "Children develop a sense of belonging to groups and communities and an understanding of the reciprocal rights and responsibilities necessary for active community participation", zh: "兒童形成團體和社區歸屬感，了解積極參與社區所必須的相互權利和責任" },
      { id: "2.2", en: "Children respond to diversity with respect", zh: "兒童尊重響應多樣性" },
      { id: "2.3", en: "Children become aware of fairness", zh: "兒童認識公平性" },
      { id: "2.4", en: "Children become socially responsible and show respect for the environment", zh: "兒童承擔起社會責任，並表示出對環境的尊重" },
    ],
  },
  {
    number: 3,
    title_en: "Children have a strong sense of wellbeing",
    title_zh: "兒童形成強烈的幸福感",
    subcodes: [
      { id: "3.1", en: "Children become strong in their social and emotional wellbeing", zh: "兒童的社交和情感幸福感日益濃厚" },
      { id: "3.2", en: "Children take increasing responsibility for their own health and physical wellbeing", zh: "兒童日益承擔起自身健康和身體幸福的責任" },
    ],
  },
  {
    number: 4,
    title_en: "Children are confident and involved learners",
    title_zh: "兒童成為自信、投入的學習者",
    subcodes: [
      { id: "4.1", en: "Children develop dispositions for learning such as curiosity, cooperation, confidence, creativity, commitment, enthusiasm, persistence, imagination and reflexivity", zh: "兒童培養學習性格，如好奇心、合作性、自信心、創造力、投入性、熱情、堅韌、想像力和反射性" },
      { id: "4.2", en: "Children develop a range of skills and processes such as problem solving, inquiry, experimentation, hypothesizing, researching and investigating", zh: "兒童培養一系列技能和方法, 如問題解決、詢問、試驗、假設、研究和調查" },
      { id: "4.3", en: "Children transfer and adapt what they have learned from one context to another", zh: "兒童將自己從某個情境中學到的東西,轉移到另一個情境中並加以調整適應" },
      { id: "4.4", en: "Children resource their own learning through playing with people, place, technologies and natural and processed materials", zh: "兒童通過與人、場所、技術以及自然和加工材料的聯繫，為自己的學習創造資源" },
    ],
  },
  {
    number: 5,
    title_en: "Children are effective communicators",
    title_zh: "兒童能有效溝通交流",
    subcodes: [
      { id: "5.1", en: "Children interact verbally and non-verbally with others for a range of purposes", zh: "兒童出於各種目的，以口頭及非口頭方式與他人展開互動" },
      { id: "5.2", en: "Children engage with a range of texts and gain meaning from these texts", zh: "兒童接觸一系列文本，並從中獲取意思" },
      { id: "5.3", en: "Children express ideas and make meaning using a range of media", zh: "兒童利用一系列媒介來表達想法、形成意思" },
      { id: "5.4", en: "Children begin to understand how symbols and pattern systems work", zh: "兒童開始理解符號和圖案系統的運作方式" },
      { id: "5.5", en: "Children use information and communication technologies to access information, investigate ideas and represent their thinking", zh: "兒童利用信息通訊技術來獲取信息、調查想法並表達自己的思維" },
    ],
  },
];

const SUBCODE_INDEX = new Map<string, EylfSubcode>(
  EYLF_OUTCOMES.flatMap((o) => o.subcodes).map((s) => [s.id, s])
);

export function getEylfText(id: string): { en: string; zh: string } | null {
  const sub = SUBCODE_INDEX.get(id);
  return sub ? { en: sub.en, zh: sub.zh } : null;
}

export const VALID_EYLF_IDS: readonly string[] = Array.from(SUBCODE_INDEX.keys());
